import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Typography } from '@mui/material';
import { formatCountdownClock } from './roadsImporter.helpers';

export interface RoadHoverEntry {
  key: string;
  label: string;
  blocked: boolean;
  // Only set when `blocked` is true: the reason text, and the absolute
  // epoch-ms deadline the countdown below ticks down to — `null` means
  // blocked indefinitely.
  blockedReason?: string;
  blockedExpiresAt?: number | null;
}

export interface RoadHoverState {
  mouseX: number;
  mouseY: number;
  entries: RoadHoverEntry[];
}

interface RoadHoverTooltipProps {
  target: RoadHoverState | null;
}

const TOOLTIP_MAX_WIDTH = 280;

export function RoadHoverTooltip({
  target,
}: RoadHoverTooltipProps): JSX.Element | null {
  const { t } = useTranslation();
  // Ticks once a second so a blocked road's countdown reads like a live
  // clock (resend-OTP style) instead of a number frozen at hover time.
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  if (!target || target.entries.length === 0) return null;

  const blockedEntries = target.entries.filter((entry) => entry.blocked);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: target.mouseY + 14,
        left: target.mouseX + 14,
        zIndex: (theme) => theme.zIndex.tooltip,
        pointerEvents: 'none',
        borderRadius: 1,
        bgcolor: 'background.paper',
        border: (theme) => `1px solid ${theme.palette.divider}`,
        boxShadow: 3,
        maxWidth: TOOLTIP_MAX_WIDTH,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.5,
          px: 1,
          py: 0.75,
        }}
      >
        {target.entries.map((entry) => (
          <Chip
            key={entry.key}
            label={entry.label}
            size="small"
            color={entry.blocked ? 'error' : 'default'}
          />
        ))}
      </Box>
      {blockedEntries.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25,
            px: 1,
            pb: 0.75,
          }}
        >
          {blockedEntries.map((entry) => (
            <Typography
              key={entry.key}
              variant="caption"
              sx={{ color: 'error.main', fontVariantNumeric: 'tabular-nums' }}
            >
              {entry.blockedReason} ·{' '}
              {entry.blockedExpiresAt == null
                ? t('roads_importer_blocked_indefinite')
                : t('roads_importer_blocked_countdown', {
                    time: formatCountdownClock(entry.blockedExpiresAt - now),
                  })}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}
